/**
 * Asientos Contables List - Finance 360
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  BookOpen,
  Plus,
  Search,
  Filter,
  Eye,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Sparkles,
  FileText,
  Edit3,
  CheckSquare,
  Copy,
  Loader2
} from 'lucide-react';
import { contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import LoadingSpinner, { LoadingPage } from '../../components/LoadingSpinner';
import ConfirmModal from '../../components/ConfirmModal';
import MonthRangePicker from '../../components/MonthRangePicker';

import useFiltroPeriodo from '../../hooks/useFiltroPeriodo';
// Toolbar button component (standard style)
function ToolbarButton({ icon: Icon, label, onClick, disabled, active, variant = 'default', badge, to }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
    success: "border border-green-400 text-green-600 hover:bg-green-50 hover:border-green-500",
    purple: "border border-purple-400 text-purple-600 hover:bg-purple-50 hover:border-purple-500",
  };

  const content = (
    <>
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
      {badge && (
        <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
          {badge}
        </span>
      )}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 pointer-events-none' : ''} relative`}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${active ? 'bg-gray-100 border-gray-400' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} relative`}
    >
      {content}
    </button>
  );
}

function ToolbarSeparator() {
  return <div className="w-px h-12 bg-gray-300 mx-1" />;
}

const estadoConfig = {
  borrador: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  contabilizado: { color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  anulado: { color: 'bg-red-100 text-red-700', icon: XCircle }
};

// Overlay de progreso con contador animado (estilo Apple: cada número hace pop + blur).
function OverlayProgreso({ info, onClose }) {
  const { tipo, total, fase, resultado } = info;
  const [count, setCount] = useState(0);
  const esGenerar = tipo === 'generar';
  const acento = esGenerar ? 'text-blue-600' : 'text-emerald-600';
  const ring = esGenerar ? 'border-blue-500' : 'border-emerald-500';
  const barra = esGenerar ? 'bg-blue-500' : 'bg-emerald-500';

  useEffect(() => {
    if (fase === 'run') {
      // Avanza 1,2,3… hacia total-1 (nunca lo completa hasta que termina de verdad).
      // Ritmo pausado para que cada número se vea nítido; acelera solo si hay muchísimos.
      const id = setInterval(() => {
        setCount(c => {
          const cap = Math.max(1, total - 1);
          if (c >= cap) return c;
          const step = total > 150 ? Math.max(1, Math.round((cap - c) / 30)) : 1;
          return Math.min(cap, c + step);
        });
      }, 220);
      return () => clearInterval(id);
    }
    if (fase === 'done') {
      const objetivo = resultado != null ? resultado : total;
      setCount(objetivo);
      const t = setTimeout(onClose, 1200);
      return () => clearTimeout(t);
    }
  }, [fase, total, resultado, onClose]);

  const done = fase === 'done';
  const objetivoBarra = done ? (resultado != null ? resultado : total) : total;
  const pct = objetivoBarra > 0 ? Math.min(100, Math.round((count / objetivoBarra) * 100)) : 0;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-gray-900/40 backdrop-blur-sm">
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 8 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 380, damping: 26 }}
        className="bg-white rounded-3xl shadow-2xl px-10 py-8 w-full max-w-sm mx-4 text-center"
      >
        {/* Anillo giratorio (o check al terminar) con el número magnificándose en el centro */}
        <div className="relative mx-auto mb-5 h-28 w-28 flex items-center justify-center">
          {!done && (
            <motion.div
              className={`absolute inset-0 rounded-full border-4 border-t-transparent ${ring}`}
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, ease: 'linear', duration: 0.9 }}
            />
          )}
          {done && (
            <motion.div
              className="absolute inset-0 rounded-full border-4 border-emerald-500"
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 400, damping: 18 }}
            />
          )}
          <AnimatePresence mode="popLayout">
            <motion.span
              key={count}
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1, filter: 'blur(0px)' }}
              exit={{ scale: 1.5, opacity: 0, filter: 'blur(2px)' }}
              transition={{ type: 'spring', stiffness: 300, damping: 26 }}
              className={`text-5xl font-bold tabular-nums ${done ? 'text-emerald-600' : acento}`}
            >
              {count}
            </motion.span>
          </AnimatePresence>
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          {done
            ? '¡Listo!'
            : esGenerar ? 'Generando asientos…' : 'Contabilizando asientos…'}
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          {done
            ? `${resultado != null ? resultado : total} ${(resultado != null ? resultado : total) === 1 ? 'asiento procesado' : 'asientos procesados'}`
            : `${count} de ${total}`}
        </p>

        {/* Barra de progreso */}
        <div className="mt-4 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
          <motion.div
            className={`h-full ${done ? 'bg-emerald-500' : barra} rounded-full`}
            animate={{ width: `${pct}%` }}
            transition={{ ease: 'easeOut', duration: 0.3 }}
          />
        </div>

        {!done && <p className="text-xs text-gray-400 mt-3">No cierres esta ventana.</p>}
      </motion.div>
    </div>
  );
}

export default function Asientos() {
  const { currentEmisor } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const [asientos, setAsientos] = useState([]);
  const [totalAsientos, setTotalAsientos] = useState(0); // solo para mostrar conteo, sin paginación
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [contabilizando, setContabilizando] = useState(false);
  // Info para el overlay de progreso: { tipo: 'generar'|'contabilizar', total }
  const [procesando, setProcesando] = useState(null);
  const [pendientes, setPendientes] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // Filters
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('');
  const [tipo, setTipo] = useState('');
  // Filtro de período unificado (MonthRangePicker). Default = mes actual.
  const [filtroFecha, setFiltroFecha] = useFiltroPeriodo('Asientos', () => {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, n.getMonth() + 1, 0).getDate();
    return {
      modo: 'mes',
      periodo: `${y}-${m}`,
      desde: `${y}-${m}-01`,
      hasta: `${y}-${m}-${String(last).padStart(2, '0')}`
    };
  });
  // Alias para no romper el deps del useEffect
  const periodo = filtroFecha;

  // Get borradores for selection
  const borradores = asientos.filter(a => a.estado === 'borrador');
  const allBorradoresSelected = borradores.length > 0 && borradores.every(a => selectedIds.includes(a.id));

  // Modal state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [showGenerarPreview, setShowGenerarPreview] = useState(false);
  const [selectedPendientes, setSelectedPendientes] = useState(new Set());

  const loadAsientos = useCallback(async () => {
    setLoading(true);
    setSelectedIds([]); // Clear selection when loading
    try {
      const desde = filtroFecha?.desde || undefined;
      const hasta = filtroFecha?.hasta || undefined;
      // Sin paginación: traemos todos los del período filtrado. El sticky thead/tfoot
      // permite scrollear adentro de la card sin perder header ni totales.
      const response = await contabilidadApi.getAsientos({
        page: 1,
        limit: 5000,
        estado: estado || undefined,
        tipo: tipo || undefined,
        search: search || undefined,
        fechaDesde: desde,
        fechaHasta: hasta
      });
      setAsientos(response.data.data);
      setTotalAsientos(response.data.pagination?.total || response.data.data?.length || 0);
    } catch (error) {
      console.error('Error loading asientos:', error);
    } finally {
      setLoading(false);
    }
  }, [estado, tipo, search, periodo]);

  const loadPendientes = async () => {
    try {
      const response = await contabilidadApi.getPendientes();
      setPendientes(response.data.data);
    } catch (error) {
      console.error('Error loading pendientes:', error);
    }
  };

  useEffect(() => {
    if (currentEmisor) {
      loadAsientos();
      loadPendientes();
    }
  }, [currentEmisor, loadAsientos]);

  const handleGenerarMasivo = () => {
    // Select all by default
    if (pendientes) {
      const allKeys = new Set();
      (pendientes.documentos || []).forEach(d => allKeys.add(`doc-${d.id}`));
      (pendientes.compras || []).forEach(d => allKeys.add(`comp-${d.id}`));
      (pendientes.fecs || []).forEach(d => allKeys.add(`fec-${d.id}`));
      setSelectedPendientes(allKeys);
    }
    setShowGenerarPreview(true);
  };

  const togglePendiente = (key) => {
    setSelectedPendientes(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const confirmarGenerarMasivo = async () => {
    setShowGenerarPreview(false);
    setProcesando({ tipo: 'generar', total: selectedPendientes.size, fase: 'run', resultado: null });
    setGenerating(true);
    let resultadoFinal = 0;
    try {
      const response = await contabilidadApi.generarAsientosMasivo({ tipo: 'all' });
      const { generados, errores } = response.data.data;
      resultadoFinal = generados || 0;

      if (generados > 0) {
        toast.success(`${generados} asientos generados exitosamente`);
      }

      if (errores && errores.length > 0) {
        console.error('Errores al generar asientos:', errores);
        const primerError = errores[0];
        toast.error(`Error en ${primerError.tipo} #${primerError.id}: ${primerError.error}`);
        if (errores.length > 1) {
          toast.error(`${errores.length - 1} errores adicionales (ver consola F12)`);
        }
      }

      loadAsientos();
      loadPendientes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al generar asientos');
    } finally {
      setGenerating(false);
      setProcesando(p => p ? { ...p, fase: 'done', resultado: resultadoFinal } : null);
    }
  };

  const handleContabilizarMasivo = () => {
    if (selectedIds.length === 0) return;

    setConfirmModal({
      isOpen: true,
      title: 'Contabilizar Seleccionados',
      message: `¿Desea contabilizar ${selectedIds.length} asiento(s) seleccionado(s)?`,
      variant: 'info',
      onConfirm: async () => {
        setProcesando({ tipo: 'contabilizar', total: selectedIds.length, fase: 'run', resultado: null });
        setContabilizando(true);
        let resultadoFinal = 0;
        try {
          const response = await contabilidadApi.contabilizarAsientosMasivo(selectedIds);
          const { exitosos, errores } = response.data.data;
          resultadoFinal = exitosos || 0;

          if (exitosos > 0) {
            toast.success(`Contabilizados: ${exitosos} asientos`);
          }

          if (errores && errores.length > 0) {
            console.error('Errores al contabilizar:', errores);
            toast.error(`${errores.length} asiento(s) con error`);
          }

          setSelectedIds([]);
          loadAsientos();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al contabilizar');
        } finally {
          setContabilizando(false);
          setProcesando(p => p ? { ...p, fase: 'done', resultado: resultadoFinal } : null);
        }
      }
    });
  };

  const cerrarProcesando = useCallback(() => setProcesando(null), []);

  const toggleSelectAll = () => {
    if (allBorradoresSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(borradores.map(a => a.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-lg border p-3 flex items-center justify-center gap-2 flex-wrap">
        <ToolbarButton
          icon={Plus}
          label="Nuevo"
          to="/contabilidad/asientos/nuevo"
          variant="primary"
        />

        <ToolbarSeparator />

        <ToolbarButton
          icon={Sparkles}
          label="Generar Auto"
          onClick={handleGenerarMasivo}
          disabled={generating || !pendientes?.total}
          variant="purple"
          badge={pendientes?.total > 0 ? pendientes.total : null}
        />

        <ToolbarSeparator />

        <ToolbarButton
          icon={CheckSquare}
          label="Contabilizar"
          onClick={handleContabilizarMasivo}
          disabled={contabilizando || selectedIds.length === 0}
          variant="success"
          badge={selectedIds.length > 0 ? selectedIds.length : null}
        />

        <ToolbarSeparator />

        <ToolbarButton
          icon={RefreshCw}
          label="Actualizar"
          onClick={() => { loadAsientos(); loadPendientes(); }}
          disabled={loading}
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-white p-4 rounded-lg border">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadAsientos()}
            placeholder="Buscar por número o concepto..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <MonthRangePicker value={filtroFecha} onChange={setFiltroFecha} />
        <select
          value={estado}
          onChange={(e) => { setEstado(e.target.value); }}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todos los estados</option>
          <option value="borrador">Borrador</option>
          <option value="contabilizado">Contabilizado</option>
          <option value="anulado">Anulado</option>
        </select>
        <select
          value={tipo}
          onChange={(e) => { setTipo(e.target.value); }}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todos los tipos</option>
          <option value="automatico">Automático</option>
          <option value="manual">Manual</option>
          <option value="ajuste">Ajuste</option>
        </select>
      </div>

      {/* Table — sticky thead + scroll interno (sin paginación, trae todos del período filtrado) */}
      <div className="bg-white rounded-xl border overflow-hidden flex flex-col h-[calc(var(--alto-contenido)-120px)]">
        {loading ? (
          <div className="p-8 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : asientos.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-2" />
            <p>No hay asientos registrados</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-auto min-h-0">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-center w-10">
                    <input
                      type="checkbox"
                      checked={allBorradoresSelected}
                      onChange={toggleSelectAll}
                      disabled={borradores.length === 0}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
                      title="Seleccionar todos los borradores"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    # / Fecha
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Concepto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Monto
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {asientos.map((asiento) => {
                  const config = estadoConfig[asiento.estado];
                  const Icon = config.icon;

                  return (
                    <tr key={asiento.id} className={`hover:bg-gray-50 ${selectedIds.includes(asiento.id) ? 'bg-green-50' : ''}`}>
                      <td className="px-4 py-3 text-center">
                        {asiento.estado === 'borrador' ? (
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(asiento.id)}
                            onChange={() => toggleSelect(asiento.id)}
                            className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">#{asiento.numero}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(asiento.fecha).toLocaleDateString('es-CR')}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900 line-clamp-1">{asiento.concepto}</p>
                        {asiento.generadoPorAi && (
                          <span className="inline-flex items-center gap-1 text-xs text-purple-600">
                            <Sparkles className="h-3 w-3" />
                            AI ({asiento.confianzaAi?.toFixed(0)}%)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 capitalize">{asiento.tipo}</span>
                        {asiento.origenTipo && (
                          <p className="text-xs text-gray-400">{asiento.origenTipo}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="font-medium text-gray-900">
                          {formatCurrency(asiento.totalDebito)}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${config.color}`}>
                          <Icon className="h-3 w-3" />
                          {asiento.estado}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Link
                            to={`/contabilidad/asientos/${asiento.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1 text-xs border border-blue-600 text-blue-600 rounded hover:bg-blue-50"
                            title="Ver asiento"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            Ver
                          </Link>
                          <Link
                            to={`/contabilidad/asientos/nuevo?copyFrom=${asiento.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-purple-500 text-purple-600 rounded hover:bg-purple-50"
                            title="Crear un asiento nuevo usando este como plantilla"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {/* Footer mínimo con conteo total — siempre visible */}
            <div className="shrink-0 flex items-center justify-between px-4 py-2 border-t bg-gray-50 text-xs text-gray-600">
              <span>{asientos.length} asiento{asientos.length !== 1 ? 's' : ''} mostrado{asientos.length !== 1 ? 's' : ''}{totalAsientos !== asientos.length ? ` de ${totalAsientos}` : ''}</span>
              {asientos.length >= 5000 && (
                <span className="text-amber-600 font-medium">⚠ Límite 5000 alcanzado — filtre por período más corto</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Confirm Modal */}
      {/* Modal Preview Generar Auto */}
      {showGenerarPreview && pendientes && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Generar Asientos Automáticos</h2>
                  <p className="text-sm text-gray-500">{pendientes.total} documento{pendientes.total !== 1 ? 's' : ''} pendiente{pendientes.total !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <button onClick={() => setShowGenerarPreview(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <XCircle className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Documentos de Venta */}
              {pendientes.documentos?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Documentos de Venta ({pendientes.documentos.length})
                  </h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="w-8 px-3 py-2"></th>
                          <th className="text-left px-3 py-2 font-medium text-gray-600">Consecutivo</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-600">Tipo</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-600">Cliente</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-600">Fecha</th>
                          <th className="text-right px-3 py-2 font-medium text-gray-600">Monto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pendientes.documentos.map(doc => (
                          <tr key={`doc-${doc.id}`} className={`hover:bg-gray-50 ${!selectedPendientes.has(`doc-${doc.id}`) ? 'opacity-40' : ''}`}>
                            <td className="px-3 py-2"><input type="checkbox" checked={selectedPendientes.has(`doc-${doc.id}`)} onChange={() => togglePendiente(`doc-${doc.id}`)} className="w-4 h-4 rounded" /></td>
                            <td className="px-3 py-2 font-mono text-xs">{doc.consecutivo}</td>
                            <td className="px-3 py-2">
                              <span className={`text-xs px-1.5 py-0.5 rounded ${doc.tipo_documento === '01' ? 'bg-blue-100 text-blue-700' : doc.tipo_documento === '03' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'}`}>
                                {doc.tipo_documento === '01' ? 'FE' : doc.tipo_documento === '03' ? 'NC' : doc.tipo_documento === '04' ? 'TE' : doc.tipo_documento}
                              </span>
                            </td>
                            <td className="px-3 py-2 truncate max-w-[200px]">{doc.receptor_nombre || '-'}</td>
                            <td className="px-3 py-2 text-gray-500">{new Date(doc.fecha_emision).toLocaleDateString('es-CR')}</td>
                            <td className="px-3 py-2 text-right font-medium">₡{parseFloat(doc.total_comprobante_crc || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Facturas de Compra */}
              {pendientes.compras?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Facturas de Compra ({pendientes.compras.length})
                  </h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="w-8 px-3 py-2"></th>
                          <th className="text-left px-3 py-2 font-medium text-gray-600">Consecutivo</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-600">Proveedor</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-600">Fecha</th>
                          <th className="text-right px-3 py-2 font-medium text-gray-600">Monto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pendientes.compras.map(doc => (
                          <tr key={`comp-${doc.id}`} className={`hover:bg-gray-50 ${!selectedPendientes.has(`comp-${doc.id}`) ? 'opacity-40' : ''}`}>
                            <td className="px-3 py-2"><input type="checkbox" checked={selectedPendientes.has(`comp-${doc.id}`)} onChange={() => togglePendiente(`comp-${doc.id}`)} className="w-4 h-4 rounded" /></td>
                            <td className="px-3 py-2 font-mono text-xs">{doc.consecutivo}</td>
                            <td className="px-3 py-2 truncate max-w-[200px]">{doc.proveedor_nombre || '-'}</td>
                            <td className="px-3 py-2 text-gray-500">{new Date(doc.fecha_emision).toLocaleDateString('es-CR')}</td>
                            <td className="px-3 py-2 text-right font-medium">₡{parseFloat(doc.total_comprobante_crc || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* FECs */}
              {pendientes.fecs?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Facturas Electrónicas de Compra ({pendientes.fecs.length})
                  </h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="w-8 px-3 py-2"></th>
                          <th className="text-left px-3 py-2 font-medium text-gray-600">Consecutivo</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-600">Proveedor</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-600">Fecha</th>
                          <th className="text-right px-3 py-2 font-medium text-gray-600">Monto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pendientes.fecs.map(doc => (
                          <tr key={`fec-${doc.id}`} className={`hover:bg-gray-50 ${!selectedPendientes.has(`fec-${doc.id}`) ? 'opacity-40' : ''}`}>
                            <td className="px-3 py-2"><input type="checkbox" checked={selectedPendientes.has(`fec-${doc.id}`)} onChange={() => togglePendiente(`fec-${doc.id}`)} className="w-4 h-4 rounded" /></td>
                            <td className="px-3 py-2 font-mono text-xs">{doc.consecutivo}</td>
                            <td className="px-3 py-2 truncate max-w-[200px]">{doc.proveedor_nombre || '-'}</td>
                            <td className="px-3 py-2 text-gray-500">{new Date(doc.fecha_emision).toLocaleDateString('es-CR')}</td>
                            <td className="px-3 py-2 text-right font-medium">₡{parseFloat(doc.total_comprobante_crc || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setShowGenerarPreview(false)}
                className="px-5 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarGenerarMasivo}
                className="px-5 py-2.5 border-2 border-purple-500 text-purple-600 rounded-lg hover:bg-purple-50 font-medium flex items-center gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Generar {selectedPendientes.size} Asiento{selectedPendientes.size !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant || 'warning'}
        confirmText="Confirmar"
      />

      {/* Overlay de progreso animado para Generar Auto / Contabilizar */}
      {procesando && (
        <OverlayProgreso info={procesando} onClose={cerrarProcesando} />
      )}
    </div>
  );
}
